import { GfdPlaybook, PacketField, SeedInitiative } from './types';
import { valueToString, visiblePacketFields } from './mapper';

function isCopyable(fieldType: PacketField['field']['fieldType']): boolean {
  return (
    fieldType === 'free_text_short' ||
    fieldType === 'free_text_long' ||
    fieldType === 'number' ||
    fieldType === 'currency' ||
    fieldType === 'reference_lookup'
  );
}

export function buildChampionScript(
  playbook: GfdPlaybook,
  seed: SeedInitiative,
  packet: PacketField[]
): string {
  const visible = visiblePacketFields(packet);
  const lines: string[] = [];

  lines.push(`# Champion script — ${seed.name}`);
  lines.push('');
  lines.push(`Prepared for: **${seed.champion.name}**, ${seed.champion.title}`);
  lines.push(`Playbook: ${playbook.name} (${playbook.version})`);
  lines.push(`Interface: ${playbook.interfaceName}`);
  lines.push('');
  lines.push('You are submitting a **Global Initiatives Intake Request** on behalf of this initiative. Work top to bottom. Where this script says **paste**, copy the block exactly. Where it says **select**, click the dropdown — do not type the value unless the field is a search box.');
  lines.push('');
  lines.push('## Critical warnings');
  playbook.warnings.forEach((w) => {
    lines.push(`- **${w.title}:** ${w.text}`);
  });
  lines.push('');
  lines.push('## 0. How to get there');
  lines.push(`- Production: ${playbook.navigation.prodUrl}`);
  lines.push(`- UAT: ${playbook.navigation.uatUrl}`);
  playbook.navigation.steps.forEach((step, i) => lines.push(`${i + 1}. ${step}`));
  lines.push('');

  let n = 1;
  playbook.sections.forEach((section) => {
    const rows = visible.filter((row) => row.field.location === section.fields[0]?.location || section.fields.some((f) => f.id === row.field.id));
    const sectionRows = visible.filter((row) => section.fields.some((f) => f.id === row.field.id));
    if (!sectionRows.length) return;
    lines.push(`## ${n}. ${section.title}`);
    lines.push(section.intro);
    lines.push('');
    sectionRows.forEach((row) => {
      lines.push(`### ${row.field.label}`);
      lines.push(`- Location: ${row.field.location}`);
      lines.push(`- Control: ${row.field.fieldType}${row.field.required === true ? ' · required' : row.field.required === 'conditional' ? ' · conditionally required' : ''}`);
      if (row.confirmInServiceNow) {
        lines.push('- **Confirm in ServiceNow:** the live option wording may differ. Pick the closest match and reply with a screenshot of the open dropdown.');
      }
      lines.push('- Clicks:');
      row.field.clickPath.forEach((step, i) => lines.push(`  ${i + 1}. ${step}`));
      lines.push(`- **Value to enter / select:**`);
      lines.push('');
      lines.push('```');
      lines.push(valueToString(row.value));
      lines.push('```');
      lines.push('');
      if (isCopyable(row.field.fieldType)) {
        lines.push('_Paste the block above. Do not paraphrase._');
      } else {
        lines.push('_Do not paste into a dropdown — click the matching option._');
      }
      if (row.field.warn) lines.push(`- Warning: ${row.field.warn}`);
      if (row.sourceSnippet) lines.push(`- Why: ${row.rationale}`);
      lines.push('');
    });
    n += 1;
    void rows;
  });

  lines.push('## Attachments (after Submit is fine)');
  playbook.attachments.forEach((a) => {
    lines.push(`- ${a.name}${a.required ? ' (required)' : ' (optional)'}: ${a.note}`);
  });
  lines.push('');
  lines.push('## After you submit');
  playbook.afterSubmit.forEach((card) => {
    lines.push(`### ${card.stage}`);
    lines.push(card.summary);
    card.requestorShould.forEach((item) => lines.push(`- ${item}`));
    lines.push('');
  });
  lines.push('---');
  lines.push('When finished, reply to the consultant with: (1) the IDEA number, (2) any dropdown whose live wording did not match this script, (3) a screenshot if Submit errored.');
  lines.push('');
  return lines.join('\n');
}

export function buildChampionEmail(
  playbook: GfdPlaybook,
  seed: SeedInitiative,
  stats: { confirmInSn: number; total: number }
): string {
  const first = (seed.champion.name || 'there').split(' ')[0];
  return [
    `Subject: GFD Stage 1 packet — ${seed.name}`,
    '',
    `Hi ${first},`,
    '',
    'Attached are a PDF walkthrough and an Excel checklist for submitting this initiative in ServiceNow Employee Center. Tick Done in Excel as you go. Work in one sitting — Save as Draft does not save.',
    '',
    `Open: ${playbook.navigation.prodUrl}`,
    'Search: Global Initiatives → Global Initiatives Intake Request → Request',
    '',
    'Paste where the packet says Paste. Click the dropdown where it says Select — do not type into a list.',
    stats.confirmInSn
      ? `${stats.confirmInSn} of ${stats.total} fields are marked Confirm in ServiceNow. Pick the closest live option; if the wording differs, screenshot the open dropdown and send it back.`
      : 'If a live dropdown does not match the packet, screenshot it and send it back.',
    '',
    'After Submit, reply with the IDEA number (IDEA#######). I will stay on email if Working Group sends it to Draft.',
    '',
    'Thanks,',
  ].join('\n');
}

export function downloadTextFile(filename: string, contents: string): void {
  const blob = new Blob([contents], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
